import React, { useEffect, useMemo, useState } from "react";
import "./Demographics.css";
import { Phone, PhoneCall, PhoneOff, Activity, Clock, MapPin, Users, Calendar, Download, Filter, X, IndianRupee } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import Datepicker from "../../components/Datepicker/Datepicker";
import UserDashboardService from "../../services/UserDashboard.service";
import AdminDashboardService from "../../services/AdminDashboard.service";
import MultiSelectDropdown from "../../components/FilterBar/MultiSelectDropdown";
import Dropdown from "../../components/Dropdown/Dropdown";
import AuthService from "../../services/Auth.service";
import toast from "react-hot-toast";
import ApiService from "../../services/Api.service";
import Loader from "../../components/Loader/Loader";
import moment from "moment";

export default function Demographics() {
  const [dashboardData, setDashboardData] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateError, setDateError] = useState("");

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const THEME = useMemo(
    () => ({
      primary: "var(--secondary)",
      primaryDark: "var(--primary-dark)",
      secondary: "var(--primary)",
      success: "var(--success)",
      warning: "var(--warning)",
      error: "var(--error)",
      gray100: "var(--gray-100)",
      gray400: "var(--gray-400)",
      gray500: "var(--gray-500)",
    }),
    []
  );

  const DONUT_COLORS = useMemo(() => [THEME.success, THEME.error], [THEME]);
  const PIE_COLORS = useMemo(
    () => [
      THEME.primary,
      "#3b82f6",
      THEME.success,
      THEME.warning,
      "#ec4899",
      "#8b5cf6",
      "#14b8a6",
    ],
    [THEME]
  );

  const fetchDetail = async () => {
    try {
      const res = await ApiService.getCompanyOptions();
      if (res.status === 200 && res.data) {
        setCompanyOptions(res.data.data || []);
        const first = (res.data.data || [])[0];
        if (first) {
          setSelectedCompany(String(first.value ?? first.id ?? ""));
        }
      } else {
        toast.error("Failed to fetch company list");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Error loading data");
    }
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      const payload = {
        company_id: selectedCompany,
        start_date: startDate? moment(startDate).format("YYYY-MM-DD"): null,
        end_date: endDate? moment(endDate).format("YYYY-MM-DD"): null,
      };

      const res = await AdminDashboardService.getDemographicsDashboardData(
        payload
      );
      setDashboardData(res?.data?.data);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  useEffect(() => {
    if (!selectedCompany) return;
    fetchDashboardData();
  }, [selectedCompany]);

  const renderKpiIcon = (iconName) => {
    switch (iconName) {
      case "phone":
        return <Phone size={20} />;
      case "phone_call":
        return <PhoneCall size={20} />;
      case "phone_missed":
        return <PhoneOff size={20} />;
      case "activity":
        return <Activity size={20} />;
      case "clock":
        return <Clock size={20} />;
      case "map_pin":
        return <MapPin size={20} />;
      case "users":
        return <Users size={20} />;
      case "indian_rupee":
        return <IndianRupee size={20} />;
      default:
        return <Phone size={20} />;
    }
  };

  const getKpiClassName = (id) => {
    switch (id) {
      case "total_calls":
        return "kpi-card kpi-total";
      case "connected_calls":
        return "kpi-card kpi-connected";
      case "missed_calls":
        return "kpi-card kpi-missed";
      case "connection_rate":
        return "kpi-card kpi-rate";
      case "avg_duration":
        return "kpi-card kpi-duration";
      case "top_state":
        return "kpi-card kpi-state";
      case "top_age_group":
        return "kpi-card kpi-age";
      case "total_revenue":
        return "kpi-card kpi-rupee";
      default:
        return "kpi-card";
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (endDate && date > endDate) {
      setDateError("Start date cannot be after end date");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date < startDate) {
      setDateError("End date cannot be before start date");
    } else {
      setDateError("");
    }
  };

  const formatCompactNumber = (value) =>
    new Intl.NumberFormat("en-IN", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

  const formatCurrencyCompact = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

  if (isLoading) {
    return <Loader />;
  }
  const hasChartData = (chartKey) => {
  const chart = dashboardData?.charts?.[chartKey];
  if (!chart) return false;
  if (Array.isArray(chart.data)) {
    return chart.data.length > 0;
  }
  return !!chart.data;
};

  return (
    <div className="demographic-dashboard">
      {/* 1. Header Area */}
      <div className="demographic-header">
        <div className="demographic-title-area">
          <h2>Call Analytics Dashboard</h2>
          <p>Overview of customer call performance and demographic insights</p>
        </div>

        <div className="demographic-actions">
          <div className="demographic-inline-filter-group">
            <div className="fb-ms">
              <Dropdown
                name="company_ids"
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                }}
                options={companyOptions || []}
                placeholder="Select Company"
              />
            </div>
          </div>

          <div className="demographic-inline-filter-wrapper">
            <button
              className="demographic-filter-btn"
              onClick={() => setShowFilterModal(!showFilterModal)}
            >
              <Filter size={16} />
              <span>Filters</span>
            </button>

            {showFilterModal && (
              <div className="demographic-inline-filter-popup">
                <div className="demographic-inline-filter-header">
                  <h4>Filter Analytics</h4>

                  <button
                    className="demographic-inline-close-btn"
                    onClick={() => setShowFilterModal(false)}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="demographic-inline-filter-body">
                  <div className="demographic-inline-filter-group">
                    <label>Start Date</label>
                    <Datepicker
                      name="startDate"
                      value={startDate}
                      onChange={handleStartDateChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select Start Date"
                      errorMessage={dateError}
                      errorClass={dateError ? true : false}
                    />
                  </div>

                  <div className="demographic-inline-filter-group">
                    <label>End Date</label>
                    <Datepicker
                      name="endDate"
                      value={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select End Date"
                      errorMessage={dateError}
                      errorClass={dateError ? true : false}
                    />
                  </div>

                  {dateError && (
                    <p className="demographic-inline-filter-error">
                      {dateError}
                    </p>
                  )}

                  <div className="demographic-inline-filter-footer">
                    <button
                      className="demographic-inline-clear-btn"
                      onClick={() => {
                        setStartDate(null);
                        setEndDate(null);
                        setDateError("");
                      }}
                    >
                      Clear
                    </button>

                    <button
                      className="demographic-inline-apply-btn"
                      onClick={() => {
                        if (!dateError) {
                          fetchDashboardData();
                          setShowFilterModal(false);
                        }
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. KPI Cards Row */}
      <div className="kpi-grid">
        {dashboardData?.summary_cards?.map((card) => (
          <div className={getKpiClassName(card.id)} key={card.id}>
            <div className="kpi-header">
              <span className="kpi-title">{card.title}</span>
              <div className="kpi-icon-wrapper">{renderKpiIcon(card.icon)}</div>
            </div>
            <div className="kpi-value-area">
              <span className="kpi-value">{card.value}</span>
              {card.change && <span className="kpi-subtext">{card.change}</span>}
              {card.trend && (
                <span className="kpi-subtext">
                  <span
                    className={
                      card.trend.startsWith("+")
                        ? "kpi-trend-up"
                        : "kpi-trend-down"
                    }
                  >
                    {card.trend}
                  </span>{" "}
                  vs yesterday
                </span>
              )}
              {card.sub_value && <span className="kpi-subtext">{card.sub_value}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* 3. 6-Chart Layout Grid */}
      <div className="charts-grid">
        {/* Chart 1 */}
        {hasChartData("age_group_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.age_group_chart.title}</h4>
          <div className="chart-container-inner">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dashboardData?.charts?.age_group_chart.data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={THEME.gray100}
                  vertical={false}
                />
                <XAxis
                  dataKey="age_group"
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip cursor={{ fill: THEME.gray100 }} />
                <Legend
                  verticalAlign="top"
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                />
                <Bar
                  name="Attempts Call"
                  dataKey="attempts"
                  fill={THEME.primary}
                  radius={[4, 4, 0, 0]}
                  barSize={12}
                />
                <Bar
                  name="Connected Calls"
                  dataKey="connected"
                  fill={THEME.success}
                  radius={[4, 4, 0, 0]}
                  barSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>)}

        {/* Chart 2 */}
        {hasChartData("connection_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.connection_chart.title}</h4>
          <div
            className="chart-container-inner"
            style={{ display: "flex", alignItems: "center" }}
          >
            <div style={{ width: "55%", height: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dashboardData?.charts?.connection_chart?.data || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {(dashboardData?.charts?.connection_chart?.data || []).map(
                      (entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                        />
                      )
                    )}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div style={{ width: "45%" }} className="custom-legend-container">
              {dashboardData?.charts?.connection_chart.data?.map((item, idx) => {
                const total =
                  dashboardData?.charts?.connection_chart.data.reduce(
                    (acc, curr) => acc + curr.value,
                    0
                  );
                const percent = Math.round((item.value / total) * 100);
                return (
                  <div className="custom-legend-item" key={idx}>
                    <div className="legend-color-label">
                      <div
                        className="legend-color-dot"
                        style={{ backgroundColor: DONUT_COLORS[idx] }}
                      ></div>
                      <span className="legend-label-text">{item.name}</span>
                    </div>
                    <span className="legend-value-text">
                      {item.value} ({percent}%)
                    </span>
                  </div>
                );
              })}
              <div
                className="custom-legend-item"
                style={{
                  borderTop: `1px solid ${THEME.gray100}`,
                  paddingTop: 8,
                  marginTop: 4,
                }}
              >
                <span className="legend-label-text" style={{ fontWeight: 700 }}>
                  Total
                </span>
                <span className="legend-value-text" style={{ fontWeight: 700 }}>
                  {dashboardData?.charts?.connection_chart.data.reduce(
                    (acc, curr) => acc + curr.value,
                    0
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>)}

        {/* Chart 3 */}
        {hasChartData("state_distribution_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.state_distribution_chart.title}</h4>
          <div className="chart-container-inner">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={dashboardData?.charts?.state_distribution_chart.data}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={THEME.gray100}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="state"
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={80}
                />
                <Tooltip cursor={{ fill: THEME.gray100 }} />
                <Bar
                  dataKey="calls"
                  fill={THEME.primary}
                  radius={[0, 4, 4, 0]}
                  barSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>)}

        {/* Chart 4 */}
        {hasChartData("product_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.product_chart.title}</h4>
          <div
            className="chart-container-inner"
            style={{ display: "flex", alignItems: "center" }}
          >
            <div style={{ width: "45%", height: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dashboardData?.charts?.product_chart.data}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={65}
                    paddingAngle={2}
                    dataKey="count"
                  >
                    {dashboardData?.charts?.product_chart.data?.map(
                      (entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      )
                    )}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div style={{ width: "55%" }} className="custom-legend-container">
              {dashboardData?.charts?.product_chart.data?.map((item, idx) => {
                const total =
                  dashboardData?.charts?.product_chart.data.reduce(
                    (acc, curr) => acc + curr.count,
                    0
                  );
                const percent = Math.round((item.count / total) * 100);
                return (
                  <div className="custom-legend-item" key={idx}>
                    <div className="legend-color-label">
                      <div
                        className="legend-color-dot"
                        style={{
                          backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                        }}
                      ></div>
                      <span
                        className="legend-label-text"
                        style={{ fontSize: 11 }}
                      >
                        {item.product_name}
                      </span>
                    </div>
                    <span className="legend-value-text" style={{ fontSize: 11 }}>
                      {item.count} ({percent}%)
                    </span>
                  </div>
                );
              })}
              <div
                className="custom-legend-item"
                style={{
                  borderTop: `1px solid ${THEME.gray100}`,
                  paddingTop: 8,
                  marginTop: 4,
                }}
              >
                <span className="legend-label-text" style={{ fontWeight: 700 }}>
                  Total
                </span>
                <span className="legend-value-text" style={{ fontWeight: 700 }}>
                  {dashboardData?.charts?.product_chart.data.reduce(
                    (acc, curr) => acc + curr.count,
                    0
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>)}

        {/* Chart 5 */}
        {hasChartData("disposition_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.disposition_chart.title}</h4>
          <div className="chart-container-inner">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dashboardData?.charts?.disposition_chart.data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={THEME.gray100}
                  vertical={false}
                />
                <XAxis
                  dataKey="category"
                  tick={{ fill: THEME.gray500, fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip cursor={{ fill: THEME.gray100 }} />
                <Bar
                  dataKey="count"
                  fill={THEME.primary}
                  radius={[4, 4, 0, 0]}
                  barSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>)}

        {/* Chart 6 */}
        {hasChartData("duration_trend_chart") && (
        <div className="chart-card">
          <h4>{dashboardData?.charts?.duration_trend_chart.title}</h4>
          <div className="chart-container-inner">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={dashboardData?.charts?.duration_trend_chart.data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="colorDuration"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={THEME.secondary}
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="95%"
                      stopColor={THEME.secondary}
                      stopOpacity={0.0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={THEME.gray100}
                  vertical={false}
                />
                <XAxis
                  dataKey="time"
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: THEME.gray500, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="duration"
                  stroke={THEME.secondary}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDuration)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>)}
      </div>
    </div>
  );
}

